import { useEffect } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Download, Printer } from "lucide-react";
import { toast } from "sonner";
import { DashboardShell } from "@/components/DashboardShell";
import { PARTNER_NAV } from "@/lib/nav";
import {
  billingStatusMeta,
  billingTypeLabel,
  fetchBillingInvoice,
  formatAmount,
  formatDate,
} from "@/lib/partner-billing";

export const Route = createFileRoute("/_authenticated/partner/billing/$invoiceId")({
  validateSearch: (search: Record<string, unknown>) => ({
    print: search.print === true || search.print === "true" ? true : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Invoice detail · Landoo Partner" },
      { name: "description", content: "View, print or download a Landoo partner invoice." },
      { property: "og:title", content: "Invoice detail · Landoo Partner" },
      { property: "og:description", content: "Invoice details for your Landoo partner account." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  errorComponent: () => (
    <DashboardShell title="Invoice" role="Partner" items={PARTNER_NAV}>
      <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
        This invoice could not be loaded.
      </div>
    </DashboardShell>
  ),
  notFoundComponent: () => (
    <DashboardShell title="Invoice" role="Partner" items={PARTNER_NAV}>
      <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
        Invoice not found.
      </div>
    </DashboardShell>
  ),
  component: BillingInvoiceDetail,
});

function BillingInvoiceDetail() {
  const { invoiceId } = Route.useParams();
  const { print } = Route.useSearch();
  const { data: inv, isLoading } = useQuery({
    queryKey: ["partner-billing-invoice", invoiceId],
    queryFn: () => fetchBillingInvoice(invoiceId),
  });

  useEffect(() => {
    if (print && inv) {
      const t = setTimeout(() => window.print(), 300);
      return () => clearTimeout(t);
    }
  }, [print, inv]);

  const st = inv ? billingStatusMeta(inv.status) : null;

  return (
    <DashboardShell title="Invoice" role="Partner" items={PARTNER_NAV}>
      <div className="no-print flex flex-wrap items-center justify-between gap-2">
        <Link
          to="/partner/billing"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-brown-dark"
        >
          <ArrowLeft className="h-4 w-4" /> Back to invoices
        </Link>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => toast.info("PDF download will be available soon.")}
            className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-2 text-xs font-semibold text-muted-foreground"
          >
            <Download className="h-4 w-4" /> Download PDF
          </button>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 rounded-full bg-coral px-3.5 py-2 text-xs font-semibold text-white shadow-soft"
          >
            <Printer className="h-4 w-4" /> Print
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="mt-4 rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
          Loading invoice…
        </div>
      )}

      {!isLoading && !inv && (
        <div className="mt-4 rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
          Invoice not found.
        </div>
      )}

      {inv && (
        <div className="print-area mt-4 rounded-2xl border border-border bg-card p-5 shadow-soft sm:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4 border-b border-border pb-5">
            <div>
              <p className="text-lg font-bold tracking-tight text-brown-dark">LANDOO</p>
              <p className="text-xs text-muted-foreground">Partner services invoice</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                Invoice number
              </p>
              <p className="font-semibold text-brown-dark">{inv.invoice_number}</p>
              {st && (
                <span
                  className={`mt-1 inline-block rounded-full px-2 py-1 text-[10px] font-semibold ${st.cls}`}
                >
                  {st.label}
                </span>
              )}
            </div>
          </div>

          <div className="grid gap-4 py-5 sm:grid-cols-3">
            <Field label="Invoice type" value={billingTypeLabel(inv.invoice_type)} />
            <Field label="Invoice date" value={formatDate(inv.invoice_date)} />
            <Field label="Due date" value={formatDate(inv.due_date)} />
            <Field label="Service" value={inv.service_type ? inv.service_type : "—"} />
            <Field label="Paid on" value={formatDate(inv.paid_at)} />
            <Field label="Cancelled on" value={formatDate(inv.cancelled_at)} />
          </div>

          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full min-w-[420px] text-left text-sm">
              <thead className="bg-secondary/50 text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Description</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-border">
                  <td className="px-3 py-3">
                    {inv.description || `${billingTypeLabel(inv.invoice_type)} charge`}
                  </td>
                  <td className="px-3 py-3 text-right">{formatAmount(inv.amount)}</td>
                </tr>
                {Number(inv.tax_percent) > 0 && (
                  <tr className="border-t border-border">
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      Tax ({inv.tax_percent}%)
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-muted-foreground">
                      {formatAmount(Number(inv.total) - Number(inv.amount))}
                    </td>
                  </tr>
                )}
                <tr className="border-t border-border bg-secondary/30">
                  <td className="px-3 py-3 font-semibold text-brown-dark">Total</td>
                  <td className="px-3 py-3 text-right font-semibold text-coral">
                    {formatAmount(inv.total || inv.amount)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {inv.notes && (
            <p className="mt-4 rounded-xl bg-secondary/40 p-3 text-xs text-muted-foreground">
              {inv.notes}
            </p>
          )}

          <p className="mt-6 text-[11px] text-muted-foreground">
            This is a system-generated invoice. Online payment collection is not enabled yet —
            please follow the payment instructions shared by the Landoo team.
          </p>
        </div>
      )}
    </DashboardShell>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm font-medium text-brown-dark">{value}</p>
    </div>
  );
}
